import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISelectedDashboardResult } from '../components/apia/administration/profiles/types';

export interface IProfileState {
  isGlobal: boolean;
  selectedDashboards: ISelectedDashboardResult[];
  queriedSelectedFuncId: Record<string, string>;
  isLoading: string;
  selectedEnv: string;
  environments: Record<string, { text: string | undefined; show: boolean }>;
  editionMode: string;
  hasChange: boolean;
}

const profileSlice = createSlice({
  name: 'profileSlice',
  initialState: {
    isGlobal: false,
    selectedDashboards: [],
    queriedSelectedFuncId: {},
    isLoading: '',
    selectedEnv: '',
    environments: {},
    editionMode: '',
    hasChange: false,
  } as IProfileState,

  reducers: {
    setEditionMode(
      state,
      {
        payload: { editionMode },
      }: PayloadAction<{
        editionMode: string;
      }>,
    ) {
      state.editionMode = editionMode;
    },
    setHasChange(state, { payload }: { payload: boolean }) {
      state.hasChange = payload;
    },
    setEnvironments(
      state,
      {
        payload: { envId, text, show },
      }: PayloadAction<{
        envId: string;
        text: string | undefined;
        show: boolean;
      }>,
    ) {
      state.selectedEnv = envId;
      state.environments[envId] = { text, show };
    },
    showCurrentEnv(
      state,
      {
        payload: { envId },
      }: PayloadAction<{
        envId: string;
      }>,
    ) {
      const keys = Object.keys(state.environments);
      for (let i = 0; i < keys.length; i += 1) {
        if (keys[i] !== envId) {
          state.environments[keys[i]].show = false;
        } else if (keys[i] === envId && state.environments[keys[i]].show) {
          state.environments[keys[i]].show = true;
        }
      }
    },
    deleteEnvironment(
      state,
      {
        payload: { envId },
      }: PayloadAction<{
        envId: string;
      }>,
    ) {
      delete state.environments[envId];
    },
    deleteAllEnvironments(state, payload: PayloadAction<{ payload: boolean }>) {
      if (payload) {
        state.environments = {};
        state.selectedEnv = '';
      }
    },
    setIsLoading(
      state,
      { payload: { isLoading } }: PayloadAction<{ isLoading: string }>,
    ) {
      state.isLoading = isLoading;
    },
    setQueriedSelectedFuncId(
      state,
      { payload }: PayloadAction<{ envId: string; selectedIds: string }>,
    ) {
      state.queriedSelectedFuncId[payload.envId] = '';
      if (state.queriedSelectedFuncId[payload.envId] === '') {
        state.queriedSelectedFuncId[payload.envId] = payload.selectedIds;
      }
    },
    deleteQueriedFuncId(
      state,
      {
        payload,
      }: PayloadAction<{ envId: string; selectedId: string | number }>,
    ) {
      const newSelectedIds =
        state.queriedSelectedFuncId[payload.envId].split(';');
      const newSelectedIdsString = newSelectedIds
        .filter((current) => {
          return current !== payload.selectedId;
        })
        .join(';');
      state.queriedSelectedFuncId[payload.envId] = newSelectedIdsString;
    },
    setDashboards(
      state,
      { payload }: PayloadAction<ISelectedDashboardResult[]>,
    ) {
      state.selectedDashboards = payload;
    },
    deleteDashboards(
      state,
      {
        payload: { id },
      }: PayloadAction<{
        id: string;
      }>,
    ) {
      state.selectedDashboards = state.selectedDashboards.filter((current) => {
        return current.id !== id;
      });
    },
    setIsGlobal(state, { payload }: PayloadAction<{ isGlobal: boolean }>) {
      state.isGlobal = payload.isGlobal;
    },
  },
});

export const profilesActions = profileSlice.actions;
export default profileSlice.reducer;
